import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import Admin from "../layout/Admin";
import spinner from "../layout/spinner.gif";
import ThemeForm from "./ThemeForm";
import { getThemes, deleteTheme } from "../../actions/template";
import { primaryButton, smallButton } from "./templateLabels";

// /template-themes: the designs a customer picks for a template on cloudprint.mn.
// A template offers them from its editor.
const TemplateThemes = ({ getThemes, deleteTheme }) => {
  // null while loading
  const [themes, setThemes] = useState(null);
  // null: no form; "new": a new theme; otherwise the theme being edited
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    setThemes(null);
    setThemes((await getThemes()) || []);
  }, [getThemes]);

  useEffect(() => {
    load();
  }, [load]);

  const remove = async (theme) => {
    if (!window.confirm(`«${theme.name}» дизайныг устгах уу?`)) return;
    if (await deleteTheme(theme.slug)) load();
  };

  return (
    <Admin>
      <div className="min-w-0">
        <div className="flex flex-wrap items-end justify-between gap-4 my-6">
          <div>
            <Link to="/templates" className="text-sm text-purple-600 dark:text-purple-400 hover:underline">
              ← Загварууд
            </Link>
            <h2 className="mt-1 text-2xl font-semibold text-gray-700 dark:text-gray-200">Дизайнууд</h2>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Хэрэглэгч загвар бөглөхдөө сонгох дэвсгэр зураг, өнгө
            </p>
          </div>
          <button
            type="button"
            className={primaryButton + " inline-flex items-center whitespace-nowrap"}
            onClick={() => setEditing("new")}
          >
            <i className="mr-2 fas fa-plus" />
            Шинэ дизайн
          </button>
        </div>

        {themes === null ? (
          <img
            src={spinner}
            className="my-6"
            style={{ width: "50px", margin: "auto", display: "block" }}
            alt="Loading..."
          />
        ) : themes.length === 0 ? (
          <p className="p-6 text-sm text-center text-gray-500 bg-white border-2 border-gray-200 border-dashed rounded-lg dark:bg-gray-800 dark:border-gray-700 dark:text-gray-400">
            Дизайн алга. Эхнийхээ зургийг оруулаад, загвар засах хэсгээс сонгуулна уу.
          </p>
        ) : (
          <div className="grid grid-cols-1 gap-4 mb-8 sm:grid-cols-2 xl:grid-cols-4">
            {themes.map((theme) => (
              <div key={theme.slug} className="overflow-hidden bg-white rounded-lg shadow-xs dark:bg-gray-800">
                <img
                  src={theme.thumbnail}
                  alt=""
                  className="block object-cover w-full bg-gray-100 dark:bg-gray-700"
                  style={{ aspectRatio: theme.orientation === "portrait" ? "210 / 297" : "297 / 210" }}
                />
                <div className="p-3">
                  <div className="flex items-center justify-between gap-2">
                    <p className="font-semibold text-gray-700 truncate dark:text-gray-200">{theme.name}</p>
                    <span className="flex flex-shrink-0 gap-1" aria-hidden="true">
                      <span className="w-4 h-4 border border-gray-300 rounded-full" style={{ background: theme.textColor }} />
                      <span className="w-4 h-4 border border-gray-300 rounded-full" style={{ background: theme.accentColor }} />
                    </span>
                  </div>
                  <p className="text-xs text-gray-500 truncate dark:text-gray-400">
                    {[theme.group, theme.orientation === "portrait" ? "Босоо" : "Хэвтээ", theme.slug]
                      .filter(Boolean)
                      .join(" · ")}
                  </p>
                  <div className="flex gap-2 mt-3">
                    <button type="button" className={smallButton} onClick={() => setEditing(theme)}>
                      Засах
                    </button>
                    <button type="button" className={smallButton} onClick={() => remove(theme)}>
                      Устгах
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {editing && (
          <ThemeForm
            theme={editing === "new" ? null : editing}
            onClose={() => setEditing(null)}
            onSaved={() => {
              setEditing(null);
              load();
            }}
          />
        )}
      </div>
    </Admin>
  );
};

TemplateThemes.propTypes = {
  getThemes: PropTypes.func.isRequired,
  deleteTheme: PropTypes.func.isRequired,
};

export default connect(null, { getThemes, deleteTheme })(TemplateThemes);
